import React from 'react';
import { useHistory } from 'react-router';
import { Grid, makeStyles } from '@material-ui/core';

import { Conditional } from '@klp/conditional';
import { Block, Typography } from '@klp/design';
import { DateTime } from '@klp/design/DateTime';
import { IconName, Illustration } from '@klp/design/Illustrations';

import { MessageCategory } from '../classes';
import { MessageBoardRoutes } from '../config';

const useStyles = makeStyles(() => ({
    summaryRow: {
        flexDirection: 'row' as const,
        marginBottom: '0.5rem',
        '& > *': {
            '&:first-child': {
                marginRight: '0.25rem',
                '&:empty': {
                    marginRight: 0,
                },
            },
            '&:last-child': {
                flex: 1,
            },
        },
    },
    messageTitle: {
        '&:hover,&:active': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
    statusIcon: {},
}));

// Type matching the frontpage feed API response
type FrontpageMessageItem = {
    id?: number;
    category: string;
    title: string;
    publishedAt?: Date;
};

interface IProps {
    messageList: Array<FrontpageMessageItem>;
}

export const MessageListSummary: React.FC<IProps> = ({ messageList }) => {
    const classes = useStyles();
    const history = useHistory();

    const handleMessageSelect = (message: FrontpageMessageItem) => () => {
        if (message.id) {
            history.push(MessageBoardRoutes.single(message.id));
        }
    };

    const renderMessageCategory = (value: string): JSX.Element => {
        const { Warning, Alert } = MessageCategory;
        switch (value) {
            case Warning:
                return (
                    <Illustration
                        icon={IconName.CIRCLE_OBS}
                        className={classes.statusIcon}
                        variantMapping={{ body1: 'span' }}
                        klpTypographyVariant="small"
                    />
                );
            case Alert:
                return (
                    <Illustration
                        icon={IconName.ALERT}
                        className={classes.statusIcon}
                        klpPalette="secondary"
                        variantMapping={{ body1: 'span' }}
                        klpTypographyVariant="small"
                    />
                );
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    };

    const renderMessageRow = (message: FrontpageMessageItem) => {
        return (
            <Block key={message.id}>
                <Typography klpTypographyVariant="xsmall">
                    <DateTime date={message.publishedAt} />
                </Typography>
                <Grid container={true} className={classes.summaryRow}>
                    <Grid item={true}>
                        {renderMessageCategory(message.category)}
                    </Grid>
                    <Grid item={true}>
                        <Typography
                            klpTypographyVariant="small"
                            onClick={handleMessageSelect(message)}
                            className={classes.messageTitle}
                        >
                            {message.title}
                        </Typography>
                    </Grid>
                </Grid>
            </Block>
        );
    };

    return (
        <Conditional
            condition={messageList && messageList.length > 0}
            // eslint-disable-next-line react/jsx-no-useless-fragment
            errorComponent={<></>}
        >
            <Grid container={true}>
                <Grid item={true} xs={12}>
                    <Block marginTop="compact">
                        {messageList.map((message) =>
                            renderMessageRow(message),
                        )}
                    </Block>
                </Grid>
            </Grid>
        </Conditional>
    );
};
